import { useTheme } from '@emotion/react'
import {
    isServerEnvironment,
    scaleImage,
    toArray,
    usePrevious,
    useScrollInfo,
    VideoQueueItemMeta,
} from '@news-mono/web-common'
import React, { Fragment, useEffect, useRef } from 'react'
import { PicturePlaceholder } from '../../../../content/Picture/PicturePlaceholder'
import { Skeleton } from '../../../../content/Skeleton/Skeleton'
import { colors } from '../../../../__styling/settings/colors'
import { getHumanDuration } from '../../utils/get-human-duration'
import {
    StyledCompactDetailsContainer,
    StyledIconChevronLeft,
    StyledIconChevronRight,
    StyledPlaylistActions,
    StyledPlaylistItemDetails,
    StyledPlaylistItemImage,
    StyledPlaylistItemMedia,
    StyledPlaylistItemPlayIcon,
    StyledPlaylistItemTitle,
    StyledPlaylistWrapper,
    StyledRotateLeftButton,
    StyledRotateRightButton,
    StyledUpNextText,
    StyledVideoPlaylist,
    StyledVideoPlaylistButton,
    StyledVideoPlaylistItem,
} from './VideoPlaylist.styled'

export interface VideoPlaylistProps {
    /** Shows an up next label */
    showUpNextLabel?: boolean
    isLoaded?: boolean
    videoQueue: VideoQueueItemMeta[]
    activeItemId: string
    onSelect: (item: VideoQueueItemMeta) => void
    onScroll: () => void
    compactMode?: boolean
    hide?: boolean
}

const slideWidthPlusPadding = 162

export const VideoPlaylist: React.FC<VideoPlaylistProps> = ({
    showUpNextLabel,
    videoQueue,
    onSelect,
    activeItemId,
    isLoaded,
    onScroll,
    compactMode,
    hide,
}) => {
    const [scrollInfo, setRef, slider] = useScrollInfo<HTMLUListElement>()
    const previousItemId = usePrevious(activeItemId)
    const previousCompactModeState = usePrevious(compactMode)
    const currentItemIndex = videoQueue.findIndex((i) => i.id === activeItemId)
    const nextItemIndex = currentItemIndex > -1 ? currentItemIndex + 1 : -1
    const nextIndex =
        nextItemIndex < videoQueue.length && nextItemIndex !== -1
            ? nextItemIndex
            : 0

    // Compact mode only shows 1 item
    const videoPlaylistQueue = compactMode
        ? videoQueue.length > 0
            ? [videoQueue[nextIndex]]
            : []
        : videoQueue

    const handleRotateRight = () => {
        scrollSlideLeftTo(slideWidthPlusPadding, true, slider)
    }

    const handleRotateLeft = () => {
        scrollSlideLeftTo(-slideWidthPlusPadding, true, slider)
    }

    // Scroll to active slide
    useEffect(() => {
        if (
            previousItemId !== activeItemId ||
            compactMode !== previousCompactModeState
        ) {
            scrollSlideLeftTo(
                nextItemIndex * slideWidthPlusPadding,
                false,
                slider,
            )
        }
    }, [
        activeItemId,
        previousItemId,
        nextItemIndex,
        slider,
        compactMode,
        previousCompactModeState,
    ])

    const upNextVisible = (showUpNextLabel && !compactMode) || false
    // If compact we just show the next video which is index=1 (index=0 is currently playing)

    return (
        <StyledPlaylistWrapper
            compactMode={compactMode}
            upNextVisible={upNextVisible}
            hide={hide}
        >
            {upNextVisible && <StyledUpNextText>Up Next</StyledUpNextText>}
            <StyledVideoPlaylist
                aria-roledescription="carousel"
                onScrollCapture={onScroll}
                ref={setRef}
            >
                {videoPlaylistQueue.map((item, i) => (
                    <VideoPlaylistItem
                        compactMode={compactMode}
                        key={i}
                        item={item}
                        onSelect={onSelect}
                        active={activeItemId === item.id}
                        accessibilityLabel={`${i + 1} of ${
                            videoQueue.length
                        } - ${item.name}`}
                    />
                ))}
                {!isLoaded && <StyledVideoPlaylistSkeleton />}
            </StyledVideoPlaylist>
            {isLoaded && !compactMode && (
                <StyledPlaylistActions>
                    <StyledRotateLeftButton
                        type="button"
                        aria-label="Go to the next slide"
                        onClick={handleRotateLeft}
                        upNextVisible={upNextVisible}
                        visible={scrollInfo.x.percentage !== 0}
                    >
                        <StyledIconChevronLeft />
                    </StyledRotateLeftButton>
                    <StyledRotateRightButton
                        type="button"
                        aria-label="Go to the next slide"
                        onClick={handleRotateRight}
                        upNextVisible={upNextVisible}
                        visible={scrollInfo.x.percentage !== 1}
                    >
                        <StyledIconChevronRight />
                    </StyledRotateRightButton>
                </StyledPlaylistActions>
            )}
        </StyledPlaylistWrapper>
    )
}

const supportsNativeSmoothScroll =
    !isServerEnvironment() && 'scrollBehavior' in document.documentElement.style

const scrollSlideLeftTo = (
    position: number,
    relative = false,
    slider: React.RefObject<HTMLElement>,
) => {
    if (!slider.current) {
        return
    }

    if (supportsNativeSmoothScroll) {
        slider.current.scrollTo({
            left: relative ? slider.current.scrollLeft + position : position,
            behavior: 'smooth',
        })
    } else {
        slider.current.scrollLeft = relative
            ? slider.current.scrollLeft + position
            : position
    }
}

export const StyledVideoPlaylistSkeleton: React.FC = () => (
    <Fragment>
        {toArray(6).map((_, i) => (
            <StyledVideoPlaylistItem key={i}>
                <PicturePlaceholder
                    loadingBackgroundColor={colors.sevennews.charade} // dark loading color
                    fixedRatio="16:9"
                    image={undefined}
                />
                <StyledPlaylistItemTitle>
                    <Skeleton dark count={2} />
                </StyledPlaylistItemTitle>
            </StyledVideoPlaylistItem>
        ))}
    </Fragment>
)

export interface VideoPlaylistItemProps {
    item: VideoQueueItemMeta
    active: boolean
    onSelect: (item: VideoQueueItemMeta) => void
    accessibilityLabel: string
    compactMode?: boolean
}

export const VideoPlaylistItem: React.FC<VideoPlaylistItemProps> = ({
    item,
    onSelect,
    active,
    accessibilityLabel,
    compactMode,
}) => {
    const { fallbackImages } = useTheme()
    const ref = useRef<HTMLLIElement>(null)
    const handleClick = () => onSelect(item)
    const imageWithFallbacks = item.posterImage || fallbackImages.crops['16:9']

    const title = <StyledPlaylistItemTitle>{item.name}</StyledPlaylistItemTitle>

    return (
        <StyledVideoPlaylistItem
            ref={ref}
            aria-roledescription="slide"
            aria-label={accessibilityLabel}
            aria-current={active}
        >
            <StyledVideoPlaylistButton
                active={active}
                title={`Click to play ${item.name}`}
                onClick={handleClick}
            >
                <StyledPlaylistItemMedia>
                    <StyledPlaylistItemImage
                        src={scaleImage(
                            imageWithFallbacks.reference,
                            140,
                            true,
                        )}
                        alt={item.name || item.description}
                    />
                    <StyledPlaylistItemDetails
                        compactMode={compactMode}
                        active={active}
                    >
                        <StyledPlaylistItemPlayIcon />
                        {item.videoType !== 'live'
                            ? getHumanDuration(item.duration)
                            : null}
                    </StyledPlaylistItemDetails>
                </StyledPlaylistItemMedia>

                {!compactMode && title}

                {compactMode && (
                    <StyledCompactDetailsContainer>
                        <StyledUpNextText>Up Next</StyledUpNextText>
                        {title}
                    </StyledCompactDetailsContainer>
                )}
            </StyledVideoPlaylistButton>
        </StyledVideoPlaylistItem>
    )
}
